import React from 'react';
import { getAssignmentConflicts } from '../../../../utils/leaderUtils';
import ActionMenu from '../../../shared/ActionMenu/ActionMenu';
import StatusBadge from '../../../shared/StatusBadge/StatusBadge';

/**
 * ListViewRow - Renders a single row in the assignment list.
 */
export default function ListViewRow({
    assignment: a,
    isConflictDetectionEnabled,
    assignmentsForConflicts,
    absencesForConflicts,
    workers,
    vehicles,
    kolonnen,
    isReadOnly,
    isSelected,
    onToggleSelect,
    onAssignmentClick,
    onAssignmentDelete,
    onAssignmentHover,
    onAssignmentHoverEnd
}) {
    const conflicts = isConflictDetectionEnabled ? getAssignmentConflicts(
        a,
        assignmentsForConflicts,
        absencesForConflicts,
        vehicles
    ) : { total: 0 };

    const hasConflict = (conflicts.total || 0) > 0;

    const formatDateRange = (a) => {
        if (!a.start_datetime) return 'undatiert';
        const s = new Date(a.start_datetime);
        const e = a.end_datetime ? new Date(a.end_datetime) : s;
        const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
        const sStr = fmt(s);
        const eStr = fmt(e);
        return sStr === eStr ? sStr : `${sStr} – ${eStr}`;
    };

    const formatTimeRange = (a) => {
        if (!a.start_datetime) return null;
        if (a.all_day || a.is_all_day) return 'ganztägig';
        const s = new Date(a.start_datetime);
        const e = new Date(a.end_datetime);
        if (s.getHours() === 0 && s.getMinutes() === 0 && ((e.getHours() === 0 && e.getMinutes() === 0) || (e.getHours() === 23 && e.getMinutes() === 59))) return 'ganztägig';
        const fmt = (d) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        return `${fmt(s)} – ${fmt(e)}`;
    };

    const actions = [
        {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
            ),
            label: 'Bearbeiten',
            onClick: () => onAssignmentClick(a),
            tooltip: 'Bearbeiten'
        },
        {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
            ),
            label: 'Löschen',
            variant: 'delete',
            onClick: () => onAssignmentDelete(a.id),
            tooltip: 'Löschen'
        }
    ];

    return (
        <tr 
            className={isSelected ? 'row-selected' : ''} 
            onClick={() => onAssignmentClick(a)}
            onMouseEnter={(e) => onAssignmentHover && onAssignmentHover(a, e)}
            onMouseLeave={onAssignmentHoverEnd}
        >
            <td className="checkbox-col" onClick={e => e.stopPropagation()}>
                <input 
                    type="checkbox" 
                    checked={isSelected} 
                    onChange={() => onToggleSelect(a.id)}
                />
            </td>
            {isConflictDetectionEnabled && (
                <td className="conflict-col" style={{ color: hasConflict ? '#ef4444' : '#e5e7eb', textAlign: 'center' }}>
                    {hasConflict ? '⚠️' : ''}
                </td>
            )}
            <td>
                <StatusBadge status={a.status || 'aktiv'} />
            </td>
            <td style={{ fontWeight: 600, textAlign: 'left', paddingLeft: 12 }}>{a.project_name}</td>
            <td style={{ color: '#64748b', textAlign: 'left' }}>{a.location || '—'}</td>
            <td>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500 }}>{formatDateRange(a)}</span>
                    {formatTimeRange(a) && (
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{formatTimeRange(a)}</span>
                    )}
                </div>
            </td>
            <td>
                <div className="leader-list-badge-container">
                    {(() => {
                        const uniqueKolonnen = [...new Set([
                            ...(a.workers || []).map(w => w.kolonne_id),
                            ...(a.vehicles || []).map(v => v.kolonne_id)
                        ])].filter(Boolean);
                        const limit = 3;
                        const displayItems = uniqueKolonnen.slice(0, limit);
                        const hasMore = uniqueKolonnen.length > limit;
                        
                        return (
                            <>
                                {displayItems.map(id => {
                                    const k = kolonnen.find(k => k.id === id);
                                    if (!k) return null;
                                    return (
                                        <span key={id} className="kolonne-badge" style={{ background: k.color || '#64748b', margin: '2px' }}>
                                            {k.internal_name || k.name}
                                        </span>
                                    );
                                })}
                                {hasMore && <span className="leader-list-badge-more">+{uniqueKolonnen.length - limit}</span>}
                                {uniqueKolonnen.length === 0 && <span style={{ color: '#94a3b8' }}>—</span>}
                            </>
                        );
                    })()}
                </div>
            </td>
            <td>
                <div className="leader-list-badge-container">
                    {(() => {
                        const items = a.workers || [];
                        const limit = 3;
                        const displayItems = items.slice(0, limit);
                        const hasMore = items.length > limit;
                        return (
                            <>
                                {displayItems.map((w, idx) => (
                                    <span key={idx} className="leader-list-resource-badge worker" title={w.worker_name}>
                                        {w.worker_name ? w.worker_name.split(' ')[0] : '???'}
                                    </span>
                                ))}
                                {hasMore && <span className="leader-list-badge-more">+{items.length - limit}</span>}
                                {items.length === 0 && <span style={{ color: '#94a3b8' }}>—</span>}
                            </>
                        );
                    })()}
                </div>
            </td>
            <td>
                <div className="leader-list-badge-container">
                    {(() => {
                        const items = a.vehicles || [];
                        const limit = 2; // Zeige max 2 Kennzeichen + Indikator
                        const displayItems = items.slice(0, limit);
                        const hasMore = items.length > limit;
                        return (
                            <>
                                {displayItems.map((v, idx) => (
                                    <span key={idx} className="license_plate leader-list-resource-badge vehicle" title={v.license_plate}>
                                        {v.license_plate || '???'}
                                    </span>
                                ))}
                                {hasMore && <span className="leader-list-badge-more">+{items.length - limit}</span>}
                                {items.length === 0 && <span style={{ color: '#94a3b8' }}>—</span>}
                            </>
                        );
                    })()}
                </div>
            </td>
            <td>
                {a.notes && (
                    <span className="notes-icon" title={a.notes}>📒</span>
                )}
            </td>
            <td>
                {!isReadOnly && (
                    <ActionMenu actions={actions} />
                )}
            </td>
        </tr>
    );
}
